import type { CommerceConfigContextValues } from '@/types/commerce';
import {
	LinkWrappers,
	type CommerceItem,
	swapAffiliateTags,
	type CommerceOffer,
	extractAsinFromUrl,
	extractAscTagFromUrl,
	decorateUrlUsingMapper,
	buildMappedValueObject,
	type CommerceKeyValueMapper,
	type CommerceLinkWrapperFnSet,
	type CommerceLinkWrapperConfig,
	type CommerceObjectValueMapper,
	commerceIdentifierSetToStringArray,
	type PermutiveCommerceAffiliateData,
} from '@themaven-net/commerce-shared';

export function dataToProps(data: CommerceKeyValueMapper<string>) {
	return buildMappedValueObject(data);
}

export function buildTrackonomicsKeyValueMapper(
	commerceConfigContext: CommerceConfigContextValues,
) {
	return {
		exp: () => commerceConfigContext.experiments,
		mcor: () => commerceConfigContext.viewCorrelator,
		ref: () => document.referrer, // TODO: Referrer might change with Continuous Scroll
		utm_source: () =>
			new URLSearchParams(document.location.search).get('utm_source') || '',
	};
}

/**
 * Builds an object to send to permutive
 */
export function permutiveKeyValueMapper(
	trackedUri: string,
	commerceItem?: CommerceItem,
	primaryOffer?: CommerceOffer,
): CommerceObjectValueMapper<PermutiveCommerceAffiliateData> {
	return {
		pageUrl: () => window.location.toString(),
		product: () => {
			let built: PermutiveCommerceAffiliateData['product'] = {
				productUrl: trackedUri,
			};
			const item = commerceItem;
			const offer = primaryOffer ?? item?.offers?.[0];

			if (item) {
				built = {
					...built,
					...(item.brand && { brand: item.brand }),
					...(item.title && { name: item.title }),
					...(item.identifiers &&
						Object.keys(item.identifiers).length > 0 && {
							identifiers: commerceIdentifierSetToStringArray(item.identifiers),
						}),
					...(item.classifications &&
						item.classifications.length > 0 && {
							categories: item.classifications,
						}),
				};
			}

			if (offer) {
				const offerPrice = Number(offer.price.substring(1));
				built = {
					...built,
					...(offerPrice !== 0 && {
						price: [
							{
								currency: 'USD',
								value: offerPrice,
							},
						],
					}),
					...(offer.retailerName && { retailer: offer.retailerName }),
					...(offer.retailerClassifications && {
						categories: offer.retailerClassifications,
					}),
				};
			}

			return built;
		},
	};
}

export function getLinkWrapperFns(
	commerceConfigContext: CommerceConfigContextValues,
): CommerceLinkWrapperFnSet {
	return {
		Skimlinks: LinkWrappers.Skimlinks,
		Trackonomics: (
			url: string,
			wrapperDefinition: CommerceLinkWrapperConfig,
		) => {
			const tagSwappedUrl = swapAffiliateTags(
				url,
				commerceConfigContext.affiliateTags,
			);
			const wrapped = LinkWrappers.Trackonomics(
				tagSwappedUrl,
				wrapperDefinition,
			);
			return decorateUrlUsingMapper(
				wrapped,
				buildTrackonomicsKeyValueMapper(commerceConfigContext),
			);
		},
	};
}

/**
 * These are added as data attributes to the anchor
 */
export function getDynamicAttributesMapper(
	href: string,
): CommerceKeyValueMapper<string> {
	return {
		'data-aps-asc-tag': () => extractAscTagFromUrl(href), // @todo handle dynamic tag replacement
		'data-aps-asin': () => extractAsinFromUrl(href),
		'data-asin': () => extractAsinFromUrl(href),
	};
}
